import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Flex } from '@chakra-ui/react';
import { createPortal } from 'react-dom';
import { toggleDrawer, toggleDrawerContent } from '../../actions/ui';
import { LoadingSlugs, Conditional, CMXIcon } from '@codametrix/ui-components';
import validationMessagesConfig from './validationMessagesConfig';
import { ButtonVariant } from '../../core/enums';
import { Button } from '../../components/button/button';
import DatePicker from 'react-datepicker';
import { InputVariant } from '../../core/enums';
import Input from '../../components/input/input';
import './create-case-styles.scss';
import 'react-datepicker/dist/react-datepicker.css';
import { launchAmplifyCase, MenuButton, MenuItem } from '@codametrix/shared-views';
import { createCaseActions, cmxDateTime, caseUtils, codeBenchActions } from '@codametrix/ui-common';
import { unwrapResult } from '@reduxjs/toolkit';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
const { openWindow } = codeBenchActions;
const { getChildWindowParams } = caseUtils;
const fallbackElement = document.createElement('div');
const CreateCase = ()=>{
    const initialCreateCaseState = {
        notesSpecialty: '',
        dateOfService: '',
        comments: '',
        alternate_visit_id: ''
    };
    const initialValidationMessage = {
        notesSpecialty: '',
        dateOfService: '',
        apiError: ''
    };
    const [createCase, setCreateCase] = useState(initialCreateCaseState);
    const [validationMessage, setValidationMessage] = useState(initialValidationMessage);
    const [selectedDate, setSelectedDate] = useState(null);
    const dispatch = useDispatch();
    const cancelCircleicon = /*#__PURE__*/ _jsx(CMXIcon, {
        svgIconName: "BiomeCancelCircle",
        svgIconClass: "create-case-menu-button",
        svgIconSize: "16"
    });
    const { activeCaseRun, isOpen, notesSpecialty, notesSpecialtyLoading, isDrawerOpen } = useSelector((state)=>state.createCase);
    const tenantId = useSelector((state)=>state.codeBench?.processInstance?.healthSystemTenantId?.uuid);
    useEffect(()=>{
        if (isOpen && activeCaseRun) {
            dispatch(createCaseActions.fetchNotesSpecialty(tenantId));
            const newAlternateVisitId = activeCaseRun.encounter_identifier?.number || '';
            setCreateCase((prevCreateCase)=>({
                    ...prevCreateCase,
                    alternate_visit_id: newAlternateVisitId
                }));
        }
    // eslint-disable-next-line
    }, [
        activeCaseRun,
        isOpen
    ]);
    useEffect(()=>{
        setCreateCase(initialCreateCaseState);
        setValidationMessage(initialValidationMessage);
        setSelectedDate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isDrawerOpen
    ]);
    const handleTextAreaChange = (event)=>{
        const { name, value } = event.target;
        setCreateCase((prevCreateCase)=>({
                ...prevCreateCase,
                [name]: value
            }));
    };
    const handleMenuItemSelect = (event, name)=>{
        const value = event.currentTarget.textContent;
        if (name && value) {
            setCreateCase((prevCreateCase)=>({
                    ...prevCreateCase,
                    [name]: value
                }));
        }
    };
    const formatCaseDate = (tenant_timezone, timestamp)=>{
        if (timestamp && tenant_timezone) {
            const formattedDate = cmxDateTime.setTimeZoneForDate(tenant_timezone, timestamp);
            return formattedDate ? cmxDateTime.format(formattedDate, cmxDateTime.FORMATS.DATE_OF_SERVICE) : null;
        }
        return null;
    };
    const removeTimeZone = (tenant_timezone, timestamp)=>{
        const shortCodeTZ = cmxDateTime.getTZShortCode(tenant_timezone, timestamp);
        if (shortCodeTZ) {
            return timestamp.replace(shortCodeTZ, '');
        }
        return timestamp;
    };
    const parseAndFormatDateToYMD = (date)=>{
        const [mm, dd, yyyy] = date.split('/');
        // Padding month and day with leading zeros if necessary
        const formattedMonth = mm.padStart(2, '0');
        const formattedDay = dd.padStart(2, '0');
        return `${yyyy}-${formattedMonth}-${formattedDay}`;
    };
    const formatDateToMDY = (date)=>{
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month}/${day}/${year}`;
    };
    const calculateLengthOfStay = (admissionDate, dischargeDate)=>{
        const admission = new Date(admissionDate);
        const discharge = new Date(dischargeDate);
        const differenceMs = discharge.getTime() - admission.getTime();
        const days = Math.ceil(differenceMs / (1000 * 60 * 60 * 24)) + 1;
        return days;
    };
    const handleFormSubmit = async ()=>{
        // Validation
        const newValidationMessages = initialValidationMessage;
        if (createCase.notesSpecialty.trim() === '') {
            newValidationMessages.notesSpecialty = validationMessagesConfig.requiredNotesSpecialty;
        } else if (createCase.dateOfService.trim() === '') {
            newValidationMessages.dateOfService = validationMessagesConfig.requiredDateOfService;
        }
        setValidationMessage(newValidationMessages);
        if (Object.values(newValidationMessages).some((message)=>message !== '')) {
            return;
        }
        try {
            const startDate = activeCaseRun.admission_datetime || activeCaseRun.earliest_datetime;
            const dateOfService = createCase.dateOfService === formatCaseDate(activeCaseRun?.tenant_timezone, startDate) ? removeTimeZone(activeCaseRun?.tenant_timezone, startDate) : parseAndFormatDateToYMD(createCase.dateOfService);
            const formattedCreateCase = {
                ...createCase,
                dateOfService
            };
            const response = unwrapResult(await dispatch(createCaseActions.submitCreateCase({
                createCase: formattedCreateCase,
                activeCaseRun
            })));
            dispatch(toggleDrawer(false));
            dispatch(toggleDrawerContent(false));
            setCreateCase(initialCreateCaseState);
            setSelectedDate(null);
            // redirect to new case
            handleOpenVersion(response);
        } catch (error) {
            setValidationMessage((prevState)=>({
                    ...prevState,
                    apiError: error?.message
                }));
        }
    };
    const handleOpenVersion = (processInstance)=>{
        if (processInstance) {
            const childWindowParams = getChildWindowParams(processInstance?.caseUid, processInstance?.guid.uuid, processInstance?.serviceLine, launchAmplifyCase);
            dispatch(openWindow(childWindowParams));
        }
    };
    const cancelCreateCase = ()=>{
        setCreateCase(initialCreateCaseState);
        setValidationMessage(initialValidationMessage);
        setSelectedDate(null);
        dispatch(toggleDrawer(false));
        dispatch(toggleDrawerContent(false));
    };
    return /*#__PURE__*/ _jsx(Conditional, {
        on: isOpen,
        children: notesSpecialtyLoading ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "25",
                            numberItems: 1
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "25",
                            numberItems: 1
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "80",
                            numberItems: 1
                        }, "loading-slugs")
                    ]
                }), document.getElementById('cmx-drawer-header') || fallbackElement),
                /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            numberItems: 3
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "60",
                            numberItems: 5
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "60",
                            numberItems: 5
                        }, "loading-slugs"),
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(LoadingSlugs, {
                            width: "60",
                            numberItems: 5
                        }, "loading-slugs")
                    ]
                }), document.getElementById('cmx-drawer-body') || fallbackElement)
            ]
        }) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Conditional, {
                    on: !!document.getElementById('cmx-drawer-header'),
                    children: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs("div", {
                            className: "create-case-header-section",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "create-case-header",
                                    "data-testid": "create-case-header",
                                    children: "Add case"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "patient-encounter-section",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            children: activeCaseRun.patient_name
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "patient-encounter-section-secondary",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        activeCaseRun.patient_identifier?.number,
                                                        " (src:",
                                                        ' ',
                                                        activeCaseRun.patient_identifier?.issuer,
                                                        ")"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "vertical-divider"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        ' ',
                                                        activeCaseRun?.date_of_birth ? cmxDateTime.format(activeCaseRun.date_of_birth, cmxDateTime.FORMATS.BIRTH_DATE) : 'N/A'
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "vertical-divider"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: activeCaseRun.patient_gender
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }), document.getElementById('cmx-drawer-header') || fallbackElement)
                    })
                }),
                /*#__PURE__*/ _jsx(Conditional, {
                    on: !!document.getElementById('cmx-drawer-body'),
                    children: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs("div", {
                            className: "create-case-body",
                            children: [
                                validationMessage.apiError && /*#__PURE__*/ _jsx("span", {
                                    className: "validation-message",
                                    children: validationMessage.apiError
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Primary Insurance"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-data",
                                            children: activeCaseRun?.primary_insurance || 'N/A'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "horizontal-divider"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Hospital Account Record"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-data",
                                            children: activeCaseRun?.encounter_identifier?.number
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "field-label",
                                                children: "Length of stay"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "field-data",
                                                children: activeCaseRun?.admission_datetime && activeCaseRun?.discharge_datetime ? `${calculateLengthOfStay(activeCaseRun.admission_datetime, activeCaseRun.discharge_datetime)} days` : 'N/A'
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Admission Date"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-data",
                                            children: activeCaseRun?.admission_datetime ? formatCaseDate(activeCaseRun?.tenant_timezone, activeCaseRun?.admission_datetime) : 'N/A'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Discharge Date"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-data",
                                            children: activeCaseRun?.discharge_datetime ? formatCaseDate(activeCaseRun?.tenant_timezone, activeCaseRun.discharge_datetime) : 'N/A'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "select-section",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Specialty *"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                /*#__PURE__*/ _jsx(MenuButton, {
                                                    label: createCase?.notesSpecialty,
                                                    icon: createCase?.notesSpecialty !== '' ? cancelCircleicon : undefined,
                                                    defaultLabel: "Specialty",
                                                    children: notesSpecialty?.filter((specialty)=>specialty?.Facility === activeCaseRun?.facility).map((specialty)=>/*#__PURE__*/ _jsx(MenuItem, {
                                                            onSelect: (event)=>handleMenuItemSelect(event, 'notesSpecialty'),
                                                            children: specialty?.['Note Specialty']
                                                        }, specialty?.Key))
                                                }),
                                                validationMessage.notesSpecialty && /*#__PURE__*/ _jsx("span", {
                                                    className: "validation-message",
                                                    children: validationMessage.notesSpecialty
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "select-section",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Date of Service *"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                /*#__PURE__*/ _jsx(DatePicker, {
                                                    minDate: activeCaseRun?.admission_datetime || activeCaseRun?.earliest_datetime ? new Date(formatCaseDate(activeCaseRun?.tenant_timezone, activeCaseRun?.admission_datetime ?? activeCaseRun?.earliest_datetime) || new Date()) : new Date(),
                                                    maxDate: activeCaseRun?.discharge_datetime ? new Date(formatCaseDate(activeCaseRun?.tenant_timezone, activeCaseRun?.discharge_datetime) || new Date()) : new Date(),
                                                    onChange: (date)=>{
                                                        setSelectedDate(date);
                                                        if (date) {
                                                            const formattedDate = formatDateToMDY(date);
                                                            setCreateCase((prevCreateCase)=>({
                                                                    ...prevCreateCase,
                                                                    dateOfService: formattedDate
                                                                }));
                                                        }
                                                    },
                                                    selected: selectedDate,
                                                    className: "date-of-service",
                                                    showPopperArrow: false,
                                                    customInput: /*#__PURE__*/ _jsxs(Flex, {
                                                        alignItems: 'center',
                                                        children: [
                                                            /*#__PURE__*/ _jsx(BiomeIcon, {
                                                                iconName: IconName.Calendar,
                                                                titleText: "Calendar"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Input, {
                                                                autoComplete: "off",
                                                                variant: InputVariant.TEXT,
                                                                value: selectedDate ? formatDateToMDY(selectedDate) : 'Select Date'
                                                            })
                                                        ]
                                                    })
                                                }),
                                                validationMessage.dateOfService && /*#__PURE__*/ _jsx("span", {
                                                    className: "validation-message",
                                                    children: validationMessage.dateOfService
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "field-label",
                                            children: "Comments"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: /*#__PURE__*/ _jsx("textarea", {
                                                className: "comments-textarea",
                                                rows: 4,
                                                placeholder: "Enter comments(optional)",
                                                name: "comments",
                                                "data-testid": "comments",
                                                value: createCase.comments,
                                                maxLength: 1024,
                                                onChange: handleTextAreaChange
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "button-section",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: ButtonVariant.SECONDARY,
                                            dataTestId: "cancelBtn",
                                            label: "Cancel",
                                            onClick: cancelCreateCase,
                                            className: "cancel-button"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: ButtonVariant.PRIMARY,
                                            dataTestId: "addCaseBtn",
                                            label: "Add Case",
                                            onClick: handleFormSubmit
                                        })
                                    ]
                                })
                            ]
                        }), document.getElementById('cmx-drawer-body') || fallbackElement)
                    })
                })
            ]
        })
    });
};
CreateCase.displayName = 'CreateCase';
export { CreateCase };
